import React, { useCallback, useEffect, useId, useState } from "react";
import type { DropdownProps } from "semantic-ui-react";
import { StyledDropdown as WWDropdown } from "@120wateraudit/waterworks";
import styled from "styled-components";
import { useGetAllSubmissionPeriodsQuery } from "src/services/state-submission";
import { useLocation, useNavigate } from "react-router-dom";
import SubmissionPeriod from "src/types/SubmissionPeriod";
type DropdownOptions = React.ComponentProps<typeof WWDropdown>["options"];

interface Props {
  setSubmissionPeriodId: (id: number) => void;
  defaultFilters?: Record<string, any>;
}
export const SubmissionPeriodDropdown: React.FC<Props> = ({
  setSubmissionPeriodId,
  defaultFilters,
}) => {
  const searchInputId = useId().replace(/:/g, '');
  const [isOpen, setIsOpen] = useState(false);
  const menuId = `submission-period-list-${searchInputId}`;
  const { search } = useLocation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<string | number | undefined>();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const submissionPeriodId = params.get("submissionPeriodId");
    if (submissionPeriodId) {
      setSubmissionPeriodId(parseInt(submissionPeriodId));
      setCurrent(submissionPeriodId);
    }
  }, [search]);

  const setDeeplinkSubmissionPeriodId = useCallback(
    (id: number) => {
      const params = new URLSearchParams(search);
      params.set("submissionPeriodId", id.toString());
      navigate({ search: params.toString() }, { replace: true });
      setSubmissionPeriodId(id);
      setCurrent(id?.toString());
    },
    [setSubmissionPeriodId, navigate, search],
  );
  const { data: queryData } = useGetAllSubmissionPeriodsQuery({
    filters: { ...defaultFilters },
    page: 1,
    pageSize: 10000,
  });

  const [dropDownOptions, setDropDownOptions] = useState<DropdownOptions>([]);

  useEffect(() => {
    if (queryData?.data && !dropDownOptions.length) {
      const opts: DropdownOptions = [];

      const { data: submissionPeriods } = queryData;

      const sortedSubmissionPeriods = [...submissionPeriods].sort((a, b) => {
        // Helper to assign priority levels:
        const getPriority = (submissionPeriod: SubmissionPeriod): number => {
          if (submissionPeriod.active && !submissionPeriod.sandbox) return 0; // Active & not sandbox
          if (!submissionPeriod.active && !submissionPeriod.sandbox) return 1; // Inactive & not sandbox
          return 2; // Sandbox (regardless of active)
        };

        return getPriority(a) - getPriority(b);
      });

      sortedSubmissionPeriods.forEach(
        ({ id, dueDate, name, locked, active }) => {
          const dueDateStringToDate = new Date(dueDate);
          const monthName = new Intl.DateTimeFormat("en-US", {
            month: "long",
          }).format(dueDateStringToDate);
          const day = new Intl.DateTimeFormat("en-US", {
            day: "2-digit",
          }).format(dueDateStringToDate);

          const optBase = `${name} - ${monthName} ${day}, ${dueDateStringToDate.getFullYear()}`;
          const optString = locked ? `${optBase} 🔒` : optBase;

          opts.push({
            value: id.toString(),
            text: optString,
          });
        },
      );

      setDropDownOptions(opts);

      const params = new URLSearchParams(search);
      const submissionPeriodId = params.get("submissionPeriodId");
      if (submissionPeriodId) {
        setSubmissionPeriodId(parseInt(submissionPeriodId));
        setCurrent(submissionPeriodId);
        return;
      }
      const firstSubmissionPeriodChoiceId = sortedSubmissionPeriods[0]?.id;

      // Set the submission period ID on initial render
      setSubmissionPeriodId(firstSubmissionPeriodChoiceId);
      setCurrent(firstSubmissionPeriodChoiceId?.toString());
    }
  }, [queryData, setSubmissionPeriodId, dropDownOptions, setDropDownOptions]);

  return (
    <DropdownContainer>
      <DropdownLabel htmlFor={`submission-period-selector-dropdown-${searchInputId}`}>Submission Period:</DropdownLabel>
      <WWDropdown
        search
        searchInput={{ id: `submission-period-selector-dropdown-${searchInputId}`, 'aria-label': 'Submission Period' }}
        options={dropDownOptions}
        onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={menuId}
        id={`submission-period-selector-dropdown-${searchInputId}`}
        onChange={(
          _event: React.SyntheticEvent<HTMLElement, Event>,
          data: DropdownProps,
        ) => {
          setDeeplinkSubmissionPeriodId(parseInt(String(data?.value)));
        }}
        value={current}
      />
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const DropdownLabel = styled.label`
  margin: 0;
  font-size: 0.875rem;
`;
